// @givmo/fundraisers-module — every line the creation wizard shows (EMC-WEB7), as drawn in the approved
// frames 09a–09o and listed in their NOTES.md copy round (Email Campaign plan, scratchpad
// `2026-10-28-fundraiser-wizard-mockups/`). Jonathan's own words there: "Name your fundraiser", "Next",
// "Skip to full editor", "Just this page", "A campaign", the campaign card's line (2026-10-28: no mention
// of board members) and "Who can create a fundraiser page?". The join setting's question, the line under
// it and the three choices are the Campaign tab's too, so they live once, in `join-requests-copy.ts`.
// The groups questions, the "Just this page" line, "No goal" and the order they come in are the
// campaign-structure redraw's (scratchpad `2026-10-29-campaign-structure/`, SPEC.md §1, §15 and §18;
// NOTES.md's copy round, Jonathan's words, 2026-10-29 and 10-30).

export const WIZARD_COPY = {
  /** The top bar's label, today's page title. */
  title: 'New fundraiser',
  /** Before the name's act, nothing is saved. */
  exit: 'Exit',
  /** Once the draft exists. */
  saveAndExit: 'Save and exit',
  skip: 'Skip to full editor',
  back: 'Back',
  next: 'Next',
  /** "Step 1" before the choice; "Step 2 of 7" once the path is known. */
  stepOf: (step: number, total: number | null) =>
    total === null ? `Step ${step}` : `Step ${step} of ${total}`,

  // ── 1. The choice ──────────────────────────────────────────────────────────────────────────────
  kindTitle: 'One page, or a campaign?',
  pageLabel: 'Just this page',
  pageLine: 'One page you run. You can make it a campaign later.',
  campaignLabel: 'A campaign',
  campaignLine:
    'People can create their own fundraiser pages under it, and everything counts toward your goal.',

  // ── 2. Groups (a campaign, where the host offers them) ─────────────────────────────────────────
  groupsTitle: 'Will people raise in groups?',
  noGroupsLabel: 'No, just fundraiser pages',
  noGroupsLine: 'People create their fundraiser pages right on your campaign.',
  groupsLabel: 'Yes, in groups',
  groupsLine: 'Like teams, schools or classrooms, each with its own page and goal.',

  // ── 3. Set up your groups (on Yes) ─────────────────────────────────────────────────────────────
  setupTitle: 'Set up your groups',
  calledTitle: 'What are they called?',
  /** The write-in under "Other…". */
  otherPlaceholder: 'e.g. Troops',
  otherLabel: 'Other…',
  runsTitle: 'Who runs each one?',
  /** "Someone you invite, like a coach": the example follows the name picked. */
  runsInvite: (example: string) => `Someone you invite, like ${example}`,
  runsInviteLine: (singular: string) => `They run their ${singular}’s page.`,
  runsAdmins: 'You and your admins',
  runsAdminsLine: (singular: string) => `You run every ${singular}’s page.`,
  runsAnyone: 'Anyone can start one',
  runsAnyoneLine: 'Anyone with the link starts one and runs it.',
  raiseTitle: 'How will group members raise?',
  raiseOwn: 'Each member creates their own fundraiser page',
  raiseShared: 'Everyone shares the group’s page',

  // ── 3. Who can create a fundraiser page (on No) ────────────────────────────────────────────────
  // The question, its line and its three choices are the Campaign tab's (`REQUESTS_COPY`).

  // ── The name ───────────────────────────────────────────────────────────────────────────────────
  nameTitlePage: 'Name your fundraiser',
  nameTitleCampaign: 'Name your campaign',
  nameLine: 'We’ll save it as a draft as you go.',
  namePlaceholder: 'e.g. Spring Appeal',
  /** On a campaign, the name's act makes the draft a campaign, which can't be undone. */
  saveAndContinue: 'Save and continue',
  campaignNote: 'It’s still a draft. From here on, it stays a campaign.',

  // ── Goal ───────────────────────────────────────────────────────────────────────────────────────
  goalTitle: 'What’s your goal?',
  goalLineCampaign: 'Every page under your campaign counts toward it.',
  /** Goes on without one. */
  noGoal: 'No goal',

  // ── Story ──────────────────────────────────────────────────────────────────────────────────────
  storyTitle: 'Tell your story',
  storyLine: 'Its first lines show when your link is shared.',

  // ── Cover photo, the last step ─────────────────────────────────────────────────────────────────
  coverTitle: 'Add a cover photo',
  coverLine: 'It’s the first thing people see on your page.',
  choosePhoto: 'Choose a photo',
  orDrag: 'or drag it here',
  coverTypes: 'PNG, JPG or WebP, up to 10 MB',
  uploading: 'Uploading…',
  replace: 'Replace',
  finish: 'Finish',
  finishNote: 'Your draft opens in the editor, where you can finish setting it up and publish.',

  /** Where Finish, or Save and exit, lands. */
  saved: 'Draft saved',
  /** A write the server refused without words of its own (the editor's shipped lines). */
  saveFailed: 'Couldn’t save your fundraiser. Please try again.',
  uploadFailed: 'Couldn’t upload this cover photo. Please try again.',
} as const;

/**
 * What a campaign's groups are called, in the list's order, each with who'd usually run one (the first
 * answer's example). Their singular and plural are set in the full editor; the wizard picks the plural.
 */
export const GROUP_NAMES = [
  { plural: 'Teams', singular: 'Team', runner: 'a coach' },
  { plural: 'Schools', singular: 'School', runner: 'a principal' },
  { plural: 'Classrooms', singular: 'Classroom', runner: 'a teacher' },
  { plural: 'Chapters', singular: 'Chapter', runner: 'a chapter lead' },
  { plural: 'Departments', singular: 'Department', runner: 'a department head' },
] as const;
